import React, { useState, useEffect } from 'react';
import { watchPredictionSnapshot } from '../../Config/matudb';
import { 
  TrendingUp, 
  BarChart3, 
  Target, 
  Brain, 
  Zap,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const PredictionIntelligence = () => {
  const [pronosticos, setPronosticos] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalPredictions: 0,
    successRate: 0,
    averageConfidence: 0,
    bestPerformingCategory: '',
    worstPerformingCategory: '',
    recentTrend: 'up',
    topPredictor: '',
    riskLevel: 'medium'
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedCategory, setSelectedCategory] = useState('todos');

  useEffect(() => {
    return loadData();
  }, [timeRange, selectedCategory]);

  const loadData = () => {
    setLoading(true);
    return watchPredictionSnapshot('abet', (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const pronosticosArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        
        // Filtrar por rango de tiempo
        const filteredByTime = filterByTimeRange(pronosticosArray, timeRange);
        
        // Filtrar por categoría
        const filteredByCategory = selectedCategory === 'todos' 
          ? filteredByTime 
          : filteredByTime.filter(p => p.category === selectedCategory);
        
        setPronosticos(filteredByCategory);
        calculateAnalytics(filteredByCategory);
      }
      setLoading(false);
    });
  };

  const filterByTimeRange = (data, range) => {
    const now = new Date();
    const daysAgo = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    }[range] || 30;

    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));
    
    return data.filter(prediction => {
      if (!prediction.createdAt) return false;
      const predictionDate = new Date(prediction.createdAt);
      return predictionDate >= cutoffDate;
    });
  };

  const calculateAnalytics = (data) => {
    if (data.length === 0) {
      setAnalytics({
        totalPredictions: 0,
        successRate: 0,
        averageConfidence: 0,
        bestPerformingCategory: '',
        worstPerformingCategory: '',
        recentTrend: 'up',
        topPredictor: '',
        riskLevel: 'medium'
      });
      return;
    }

    const total = data.length;
    const completed = data.filter(p => p.state === 'ganado' || p.state === 'perdido');
    const won = data.filter(p => p.state === 'ganado');
    const successRate = completed.length > 0 ? (won.length / completed.length) * 100 : 0;
    
    const avgConfidence = data.reduce((sum, p) => sum + (p.confidence || 0), 0) / total;
    
    // Análisis por categoría
    const categoryStats = {};
    data.forEach(p => {
      const category = p.category || 'otros';
      if (!categoryStats[category]) {
        categoryStats[category] = { total: 0, won: 0, lost: 0 };
      }
      categoryStats[category].total++;
      if (p.state === 'ganado') categoryStats[category].won++;
      if (p.state === 'perdido') categoryStats[category].lost++;
    });

    let bestCategory = '';
    let worstCategory = '';
    let bestRate = 0;
    let worstRate = 100;

    Object.keys(categoryStats).forEach(category => {
      const stats = categoryStats[category];
      const rate = stats.total > 0 ? (stats.won / (stats.won + stats.lost)) * 100 : 0;
      if (rate > bestRate && stats.total >= 3) {
        bestRate = rate;
        bestCategory = category;
      }
      if (rate < worstRate && stats.total >= 3) {
        worstRate = rate;
        worstCategory = category;
      }
    });

    // Análisis de tendencia reciente
    const recentData = data.slice(-10);
    const recentWon = recentData.filter(p => p.state === 'ganado').length;
    const recentRate = recentData.length > 0 ? (recentWon / recentData.length) * 100 : 0;
    const recentTrend = recentRate > successRate ? 'up' : recentRate < successRate ? 'down' : 'stable';

    // Nivel de riesgo basado en confianza vs éxito
    let riskLevel = 'medium';
    if (successRate > 70 && avgConfidence > 80) riskLevel = 'low';
    else if (successRate < 50 || avgConfidence < 60) riskLevel = 'high';

    setAnalytics({
      totalPredictions: total,
      successRate: Math.round(successRate * 10) / 10,
      averageConfidence: Math.round(avgConfidence * 10) / 10,
      bestPerformingCategory: bestCategory,
      worstPerformingCategory: worstCategory,
      recentTrend,
      topPredictor: 'Super Admin', // Por ahora hardcodeado
      riskLevel
    });
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-400';
      case 'medium': return 'text-yellow-400';
      case 'high': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="text-green-400" size={20} />;
      case 'down': return <TrendingUp className="text-red-400 transform rotate-180" size={20} />;
      default: return <BarChart3 className="text-blue-400" size={20} />;
    }
  };

  const generateInsights = () => {
    const insights = [];
    
    if (analytics.successRate > 70) {
      insights.push({
        type: 'positive',
        message: 'Excelente tasa de éxito. Mantén la estrategia actual.',
        icon: CheckCircle
      });
    } else if (analytics.successRate < 50) {
      insights.push({
        type: 'warning',
        message: 'Tasa de éxito baja. Revisa los criterios de selección.',
        icon: AlertTriangle
      });
    }

    if (analytics.averageConfidence > 85) {
      insights.push({
        type: 'info',
        message: 'Confianza muy alta. Considera ser más conservador.',
        icon: Brain
      });
    }

    if (analytics.recentTrend === 'down') {
      insights.push({
        type: 'warning',
        message: 'Tendencia reciente a la baja. Analiza los últimos pronósticos.',
        icon: TrendingUp
      });
    }

    return insights;
  };

  const insights = generateInsights();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white">Inteligencia de Pronósticos</h2>
        <p className="text-white/60">Análisis avanzado y insights para mejorar la precisión</p>
      </div>

      {/* Controles */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
        <div className="flex flex-col md:flex-row gap-4">
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Rango de Tiempo</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="1y">Último año</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-white/60 mb-2">Categoría</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todas las categorías</option>
              <option value="futbol">Fútbol</option>
              <option value="basketball">Basketball</option>
              <option value="tenis">Tenis</option>
              <option value="otros">Otros</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={loadData}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw size={16} />
              <span>Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Total Pronósticos</p>
              <p className="text-2xl font-bold text-white">{analytics.totalPredictions}</p>
            </div>
            <Target className="text-blue-400" size={24} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Tasa de Éxito</p>
              <p className="text-2xl font-bold text-green-400">{analytics.successRate}%</p>
            </div>
            <CheckCircle className="text-green-400" size={24} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Confianza Promedio</p>
              <p className="text-2xl font-bold text-yellow-400">{analytics.averageConfidence}%</p>
            </div>
            <Brain className="text-yellow-400" size={24} />
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-4 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm">Nivel de Riesgo</p>
              <p className={`text-2xl font-bold ${getRiskColor(analytics.riskLevel)}`}>
                {analytics.riskLevel.toUpperCase()}
              </p>
            </div>
            <AlertTriangle className={getRiskColor(analytics.riskLevel)} size={24} />
          </div>
        </div>
      </div>

      {/* Insights y Análisis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Insights */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Zap className="text-yellow-400" size={20} />
            <span>Insights Inteligentes</span>
          </h3>
          
          <div className="space-y-3">
            {insights.length > 0 ? (
              insights.map((insight, index) => {
                const Icon = insight.icon;
                return (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-white/5 rounded-lg">
                    <Icon className={`mt-1 ${
                      insight.type === 'positive' ? 'text-green-400' :
                      insight.type === 'warning' ? 'text-yellow-400' :
                      'text-blue-400'
                    }`} size={16} />
                    <p className="text-white text-sm">{insight.message}</p>
                  </div>
                );
              })
            ) : (
              <p className="text-white/60 text-sm">No hay insights específicos para mostrar.</p>
            )}
          </div>
        </div>

        {/* Análisis de Categorías */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <BarChart3 className="text-blue-400" size={20} />
            <span>Análisis por Categoría</span>
          </h3>
          
          <div className="space-y-4">
            {analytics.bestPerformingCategory && (
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-400 font-medium">Mejor Categoría</p>
                <p className="text-white">{analytics.bestPerformingCategory}</p>
              </div>
            )}
            
            {analytics.worstPerformingCategory && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 font-medium">Categoría a Mejorar</p>
                <p className="text-white">{analytics.worstPerformingCategory}</p>
              </div>
            )}
            
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 font-medium">Tendencia Reciente</p>
              <div className="flex items-center space-x-2 mt-1">
                {getTrendIcon(analytics.recentTrend)}
                <span className="text-white">
                  {analytics.recentTrend === 'up' ? 'En alza' :
                   analytics.recentTrend === 'down' ? 'En baja' : 'Estable'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recomendaciones */}
      <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6 border border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Brain className="text-purple-400" size={20} />
          <span>Recomendaciones IA</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg">
            <h4 className="text-blue-400 font-medium mb-2">Optimización de Confianza</h4>
            <p className="text-white/80 text-sm">
              {analytics.averageConfidence > 80 
                ? 'La confianza promedio es alta. Considera ser más selectivo con los pronósticos.'
                : 'La confianza promedio es moderada. Puedes ser más agresivo en tus predicciones.'
              }
            </p>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-lg">
            <h4 className="text-green-400 font-medium mb-2">Estrategia de Categorías</h4>
            <p className="text-white/80 text-sm">
              {analytics.bestPerformingCategory 
                ? `Enfócate en ${analytics.bestPerformingCategory} para maximizar ganancias.`
                : 'Analiza más datos para identificar las mejores categorías.'
              }
            </p>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg">
            <h4 className="text-yellow-400 font-medium mb-2">Gestión de Riesgo</h4>
            <p className="text-white/80 text-sm">
              {analytics.riskLevel === 'high' 
                ? 'Nivel de riesgo alto. Revisa tu estrategia y considera reducir la exposición.'
                : analytics.riskLevel === 'low'
                ? 'Nivel de riesgo bajo. Puedes aumentar la agresividad de tus pronósticos.'
                : 'Nivel de riesgo moderado. Mantén la estrategia actual.'
              }
            </p>
          </div>
          
          <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg">
            <h4 className="text-purple-400 font-medium mb-2">Tendencias Temporales</h4>
            <p className="text-white/80 text-sm">
              {analytics.recentTrend === 'up' 
                ? 'Tendencia positiva. La estrategia actual está funcionando bien.'
                : analytics.recentTrend === 'down'
                ? 'Tendencia negativa. Revisa los últimos pronósticos y ajusta la estrategia.'
                : 'Tendencia estable. Mantén la consistencia en tu enfoque.'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PredictionIntelligence; 